import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Award, Check, Columns3, EyeOff, FilePen, ScrollText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  OVERRIDE_KIND_LABELS,
  PANELIST_ROLE_LABELS,
  RECOMMENDATION_LABELS,
  SEVERITY_LABELS,
  STAGE_DECISION_OUTCOME_LABELS,
  isPassing,
  type DebriefPanelistView,
  type DebriefView,
  type RoundOutcome,
  type StageDecisionOutcome,
  type StageDecisionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip, type ChipTone } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneIcon, toneCardClass } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDate, formatDateTime } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useRoom } from '../../../shell/SocketProvider';
import { useRequiredSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { selectionApi } from '../../comparison/api/comparison-api';
import { debriefKey, decisionsApi, useDebrief } from '../api/decisions-api';
import { DecisionBar, type DecisionChoice } from '../components/DecisionBar';
import styles from './Debrief.module.css';
import sheet from '../../../components/SheetTable.module.css';

/**
 * DEC-01 — one application's one round: every scorecard side by side, the round's verdict
 * worked out from §L3 and §L4, and the decision bar (F12.03–F12.07). Where N-41, N-42,
 * N-43 and N-44 land. Built for the hiring manager's phone first (06-qol §10).
 */
export function DebriefPage({ interviewId }: { readonly interviewId: string }): ReactNode {
  const query = useDebrief(interviewId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const session = useRequiredSession();
  const [pending, setPending] = useState<StageDecisionOutcome | null>(null);
  // The job's room carries the hints that the last scorecard arrived or the round was decided elsewhere.
  useRoom(query.data ? `job:${query.data.jobId}` : null);

  if (query.isPending) return <SkeletonLines count={12} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this debrief" contact="The recruiter for this job" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/interviews' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const view = query.data;

  /** Act first (§5a rule 1): the decision shows at once, and rolls back visibly if refused. */
  async function record(choice: DecisionChoice): Promise<void> {
    const key = debriefKey(interviewId);
    const before = queryClient.getQueryData<DebriefView>(key);
    setPending(choice.outcome);
    queryClient.setQueryData<DebriefView>(key, (current) => (current ? { ...current, decision: provisional(current, choice, session.fullName) } : current));
    try {
      const next = await decisionsApi.record(interviewId, { ...choice, version: view.applicationVersion, poolIds: [...choice.poolIds], newPoolNames: [...choice.newPoolNames] });
      queryClient.setQueryData(key, next);
      toasts.show({ tone: 'success', message: doneWords(view, choice.outcome), detail: view.candidateName });
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      toasts.show({ tone: 'danger', message: "The decision wasn't recorded", detail: errorMessage(error) });
    } finally {
      setPending(null);
    }
  }

  return (
    <div className={styles.screen}>
      <Link to="/interviews/$interviewId/scorecards" params={{ interviewId }} className={styles.back}>
        ← Scorecards
      </Link>
      <Card
        title={`Debrief — ${view.candidateName}`}
        meta={`${view.jobTitle} · ${view.round.name} (round ${view.round.number} of ${view.round.count})${view.interviewedAt ? ` · interviewed ${formatDateTime(view.interviewedAt)}` : ''}`}
      >
        <div className={styles.row}>
          <Link to="/applications/$applicationId" params={{ applicationId: view.applicationId }} className={styles.inlineLink}>
            The application
          </Link>
          <Link to="/jobs/$jobId/$tab" params={{ jobId: view.jobId, tab: 'decisions' }} className={styles.inlineLink}>
            <ScrollText size={14} aria-hidden /> Decisions log
          </Link>
        </div>
      </Card>

      {view.isHiddenUntilYouSubmit || !view.outcome ? (
        <BlindNotice interviewId={interviewId} />
      ) : (
        <>
          <RoundResult view={view} outcome={view.outcome} />
          {/* Scorecards filled before C5 scored rubric lines; they still show side by side. */}
          {view.criteria.length > 0 && <ScoreTable view={view} />}
          <WrittenNotes view={view} />
        </>
      )}

      {view.decision ? (
        <DecisionRecord decision={view.decision} view={view} isSaving={pending !== null} />
      ) : (
        <DecisionBar view={view} pending={pending} onRecord={(choice) => void record(choice)} />
      )}
    </div>
  );
}

function BlindNotice({ interviewId }: { readonly interviewId: string }): ReactNode {
  return (
    <Card title="Submit your own scorecard first">
      <p className={styles.quiet}>
        <EyeOff size={14} aria-hidden /> You sat on this round and it is blind: the others' scorecards open once yours is in.
      </p>
      <Link to="/interviews/$interviewId/scorecard" params={{ interviewId }} className={styles.linkButton}>
        <FilePen size={16} aria-hidden /> Open your scorecard
      </Link>
    </Card>
  );
}

// --- The round result (F12.02, §L3 as ADR 0077 amends it) ------------------------------------

/**
 * C5: the weighted average of the panel's marks, each mark with the share it carries. There is no
 * rule and no pass mark to tick: once every counting scorecard is in, the decider makes the call.
 */
function RoundResult({ view, outcome }: { readonly view: DebriefView; readonly outcome: RoundOutcome }): ReactNode {
  const area = useToneArea();
  const isOpen = outcome.verdict === 'open';
  const marked = view.panelists.filter((panelist) => panelist.share > 0);
  return (
    <section className={`${styles.verdict} ${toneCardClass()}`} data-tone={area.tone} aria-label="The round's result">
      <div className={styles.verdictHead}>
        <ToneIcon tone={area.tone} icon={area.icon} />
        <Chip tone={isOpen ? 'success' : 'warning'} emphasis="solid">
          {isOpen ? 'Ready to decide' : 'Waiting'}
        </Chip>
        <h2 className={styles.verdictTitle}>{isOpen ? 'Every mark is in — the round is yours to decide' : 'Waiting for scorecards'}</h2>
        <span className={styles.bigScore} title="The round result — the weighted average of the panel's marks, out of 5">
          {outcome.roundScore === null ? EMPTY : outcome.roundScore.toFixed(2)}
        </span>
      </div>
      {marked.length > 0 && (
        <ul className={styles.conditions}>
          {marked.map((panelist) => (
            <li key={panelist.scorecardId} className={styles.condition_met}>
              <Check size={16} aria-hidden />
              <span>
                {panelist.name}: {panelist.panelistScore === null ? EMPTY : panelist.panelistScore} · {Math.round(panelist.share * 100)}% of the result
              </span>
            </li>
          ))}
        </ul>
      )}
      {!isOpen && view.waitingOnNames.length > 0 && (
        <p className={styles.verdictNote}>
          Waiting for {listOf(view.waitingOnNames)}. A round is never decided with a scorecard missing. Once the chase reaches HR, they can waive it on the scorecards page.
        </p>
      )}
    </section>
  );
}

// --- Scorecards side by side (F12.01) -----------------------------------------------------------

function ScoreTable({ view }: { readonly view: DebriefView }): ReactNode {
  if (view.panelists.length === 0) return <p className={styles.quiet}>Nobody sat on this round.</p>;
  return (
    <TableSection title="Scorecards side by side" meta="Lines two or more points apart are marked — disagreement is worth a conversation">
      <div className={styles.tableWrap}>
        <table className={sheet.sheet}>
          <thead>
            <tr>
              <th scope="col" className={styles.criterionHead}>
                Line
              </th>
              {view.panelists.map((panelist) => (
                <th key={panelist.scorecardId} scope="col">
                  <PanelistHead panelist={panelist} />
                </th>
              ))}
              <th scope="col" className={styles.averageHead}>
                Average
              </th>
            </tr>
          </thead>
          <tbody>
            {view.criteria.map((criterion) => (
              <tr key={criterion.key} className={criterion.isDisagreement ? styles.disagreement : undefined}>
                <th scope="row" className={styles.criterionCell}>
                  <span className={styles.criterionStack}>
                    <span className={styles.criterionLabel}>{criterion.label}</span>
                    <span className={styles.row}>
                      {criterion.isCritical && (
                        <Chip tone={criterion.isCriticalBlocked ? 'danger' : 'neutral'} emphasis={criterion.isCriticalBlocked ? 'solid' : 'tonal'}>
                          Critical
                        </Chip>
                      )}
                      {criterion.isDisagreement && <Chip tone="warning">2+ apart</Chip>}
                    </span>
                  </span>
                </th>
                {view.panelists.map((panelist) => {
                  const entry = panelist.scores.find((score) => score.key === criterion.key);
                  return (
                    <td key={panelist.scorecardId} data-label={panelist.name} className={panelist.role === 'observer' ? styles.observerCell : undefined}>
                      <span className={entry?.isNotAssessed ? styles.notAssessed : styles.score}>{entry?.isNotAssessed ? 'Not assessed' : (entry?.score ?? EMPTY)}</span>
                      {entry?.comment && <span className={styles.comment}>{entry.comment}</span>}
                    </td>
                  );
                })}
                <td data-label="Average" className={criterion.isCriticalBlocked ? styles.averageBlocked : styles.average}>
                  {criterion.average === null ? EMPTY : criterion.average.toFixed(1)}
                </td>
              </tr>
            ))}
            <tr className={styles.totalRow}>
              <th scope="row" className={styles.criterionCell}>
                Panelist score
              </th>
              {view.panelists.map((panelist) => (
                <td key={panelist.scorecardId} data-label={panelist.name} className={styles.score}>
                  {panelist.panelistScore === null ? EMPTY : panelist.panelistScore.toFixed(2)}
                </td>
              ))}
              <td data-label="Round score" className={styles.average}>
                {view.outcome?.roundScore === null || view.outcome === null ? EMPTY : view.outcome.roundScore.toFixed(2)}
              </td>
            </tr>
            <tr className={styles.totalRow}>
              <th scope="row" className={styles.criterionCell}>
                Final call
              </th>
              {view.panelists.map((panelist) => (
                <td key={panelist.scorecardId} data-label={panelist.name}>
                  {panelist.recommendation ? (
                    <Chip tone={isPassing(panelist.recommendation) ? 'success' : 'danger'} emphasis="solid">
                      {RECOMMENDATION_LABELS[panelist.recommendation]}
                    </Chip>
                  ) : (
                    EMPTY
                  )}
                </td>
              ))}
              <td data-label="Passed" className={styles.average}>
                {EMPTY}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </TableSection>
  );
}

function PanelistHead({ panelist }: { readonly panelist: DebriefPanelistView }): ReactNode {
  return (
    <span className={styles.panelistHead}>
      <span className={styles.panelistName}>{panelist.name}</span>
      <span className={styles.row}>
        {panelist.role !== 'counting' && !panelist.isOffPanel && <Chip tone="neutral">{panelist.role === 'observer' ? 'Observer — not counted' : PANELIST_ROLE_LABELS[panelist.role]}</Chip>}
        {panelist.isOffPanel && <Chip tone="neutral">Left the panel — not counted</Chip>}
        {panelist.status === 'draft' && <Chip tone="warning">Not submitted</Chip>}
        {panelist.status === 'waived' && <Chip tone="neutral">Waived</Chip>}
        {panelist.versionNumber > 1 && <Chip tone="neutral">v{panelist.versionNumber}</Chip>}
      </span>
      {/* §L16: context, never an adjustment — the decider reads a 3 from a harsh scorer for what it is. */}
      {panelist.severity && <span className={styles.severity}>{panelist.name.split(' ')[0]} {SEVERITY_LABELS[panelist.severity]}</span>}
    </span>
  );
}

function WrittenNotes({ view }: { readonly view: DebriefView }): ReactNode {
  const written = view.panelists.filter((panelist) => panelist.notes || panelist.waiverReason);
  if (written.length === 0) return null;
  return (
    <Card title="What the panel wrote">
      <div className={styles.notesList}>
        {written.map((panelist) => (
          <div key={panelist.scorecardId} className={styles.noteBlock}>
            <span className={styles.panelistName}>
              {panelist.name}
              {panelist.submittedAt && <span className={styles.quietInline}> · {formatDateTime(panelist.submittedAt)}</span>}
            </span>
            <p className={styles.noteText}>{panelist.status === 'waived' ? `Waived: "${panelist.waiverReason ?? EMPTY}"` : panelist.notes}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

// --- The recorded decision (F12.03) ---------------------------------------------------------------

function DecisionRecord({ decision, view, isSaving }: { readonly decision: StageDecisionView; readonly view: DebriefView; readonly isSaving: boolean }): ReactNode {
  const isCompareOn = useModule('compare');
  const tone: ChipTone = decision.outcome === 'advance' ? 'success' : decision.outcome === 'reject' ? 'danger' : 'attention';
  return (
    <Card title={isSaving ? 'Recording the decision…' : 'Decision recorded'} meta={`${decision.decidedByName ?? 'Someone'} · ${formatDateTime(decision.decidedAt)}`}>
      <div className={styles.recordBody}>
        <div className={styles.row}>
          <Chip tone={tone} emphasis="solid">
            {decision.outcome === 'advance' ? (view.round.nextRoundName ? `Advanced to ${view.round.nextRoundName}` : 'Passed — on to final selection') : STAGE_DECISION_OUTCOME_LABELS[decision.outcome]}
          </Chip>
          {decision.wasTieBroken && (
            <Chip tone="attention" emphasis="solid">
              Tie broken by {decision.decidedByName ?? 'the tie-breaker'}
            </Chip>
          )}
          {decision.wasOverridden && (
            <Chip tone="warning" emphasis="solid">
              Override
            </Chip>
          )}
        </div>
        {decision.reasonLabel && <p className={styles.recordLine}>Reason: {decision.reasonLabel}</p>}
        {decision.wasOverridden && (
          <p className={styles.recordLine}>
            Went against {decision.overrideKinds.map((kind) => OVERRIDE_KIND_LABELS[kind]).join(' and ')}: "{decision.overrideReason ?? EMPTY}"
          </p>
        )}
        {decision.note && <p className={styles.recordLine}>Note: {decision.note}</p>}
        {decision.holdExpiresAt && <p className={styles.recordLine}>On hold until {formatDate(decision.holdExpiresAt)} — released to an offer or a pool, or kept longer, on the comparison.</p>}
        {/* R8 (F24.11): the last round passed — select them here, Compare on or off. */}
        {view.selection && <SelectFromRound view={view} selection={view.selection} />}
        {/* S22: a finalist and a silver medalist are both decided on from DEC-02, while Compare is on. */}
        {isCompareOn && (decision.outcome === 'hold' || (decision.outcome === 'advance' && !view.round.nextRoundName)) && (
          <Link to="/jobs/$jobId/$tab" params={{ jobId: view.jobId, tab: 'compare' }} className={styles.inlineLink}>
            <Columns3 size={14} aria-hidden /> Open the comparison
          </Link>
        )}
        {decision.latencyMinutes !== null && <p className={styles.quiet}>Decided {latencyWords(decision.latencyMinutes)} after the last scorecard came in.</p>}
        <p className={styles.quiet}>The round's scorecards are now locked.</p>
      </div>
    </Card>
  );
}

/**
 * F24.11 "Select" on a last-round pass (§L33). One click, shown at once (§5a rule 1); with Offers
 * off it makes no offer, and N-92 tells the recruiter, the requester and the line manager.
 */
function SelectFromRound({ view, selection }: { readonly view: DebriefView; readonly selection: NonNullable<DebriefView['selection']> }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isSelecting, setIsSelecting] = useState(false);
  const isSelected = selection.isSelected || isSelecting;

  async function select(): Promise<void> {
    setIsSelecting(true);
    try {
      await selectionApi.select(view.jobId, view.applicationId, view.applicationVersion);
      // §5a rule 3: hold the shown state until the refetch lands.
      await Promise.all([queryClient.invalidateQueries({ queryKey: debriefKey(view.interviewId) }), queryClient.invalidateQueries({ queryKey: ['comparison'] }), queryClient.invalidateQueries({ queryKey: ['jobs'] })]);
      show({ tone: 'success', message: `${view.candidateName} is selected`, detail: 'The recruiter, the requester and the line manager have been told.' });
    } catch (error) {
      show({ tone: 'danger', message: `${view.candidateName} wasn't selected`, detail: errorMessage(error) });
    } finally {
      setIsSelecting(false);
    }
  }

  if (isSelected) {
    return (
      <div className={styles.row}>
        <Chip tone="success" emphasis="solid">
          Selected
        </Chip>
        <Link to="/jobs/$jobId/$tab" params={{ jobId: view.jobId, tab: 'selected' }} className={styles.inlineLink}>
          See everyone selected
        </Link>
      </div>
    );
  }
  if (!selection.canSelect) return selection.blockedReason ? <p className={styles.quiet}>{selection.blockedReason}</p> : null;
  return (
    <div className={styles.row}>
      <Button variant="solid" icon={Award} onClick={() => void select()}>
        Select {view.candidateName}
      </Button>
    </div>
  );
}

// --- Helpers -------------------------------------------------------------------------------------

function provisional(view: DebriefView, choice: DecisionChoice, myName: string): StageDecisionView {
  return {
    id: 'pending',
    outcome: choice.outcome,
    decidedByName: myName,
    decidedAt: new Date().toISOString(),
    roundScore: view.outcome?.roundScore ?? null,
    rule: null,
    reasonCode: choice.reasonCode ?? null,
    reasonLabel: view.rejectionReasons.find((reason) => reason.code === choice.reasonCode)?.label ?? null,
    note: choice.note,
    wasTieBroken: false,
    wasOverridden: false,
    overrideKinds: [],
    overrideReason: null,
    wasBlockedByCritical: false,
    holdExpiresAt: null,
    latencyMinutes: null,
  };
}

function doneWords(view: DebriefView, outcome: StageDecisionOutcome): string {
  if (outcome === 'advance') return view.round.nextRoundName ? `Advanced to ${view.round.nextRoundName}` : 'Passed — on to final selection';
  return outcome === 'hold' ? 'Put on hold' : 'Rejected';
}

function latencyWords(minutes: number): string {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${Math.round(hours / 24)} days`;
}

function listOf(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}
